import { companyDomainService } from "@/api/acendex/company";
import { signal } from "@preact-signals/safe-react";
import { useEffect, useRef, useState } from "react";
import { imageConfig } from "../../../../config/config";

const currentIndex = signal(0);
const isAnimating = signal(false);

// 添加自动播放控制信号
const autoplayPaused = signal(false);

const getVisibleItems = () => {
  if (typeof window !== "undefined") {
    const width = window.innerWidth;
    if (width <= 600) return 1;
    if (width <= 900) return 2;
    if (width <= 1200) return 3;
    return 4;
  }
  return 4;
};

export function SwiperSection() {
  const requestCompanyDomain = companyDomainService.useFetch({
    isInit: true,
  });

  const companyDomain = requestCompanyDomain.data.value;
  // 添加触摸事件状态
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const swiperRef = useRef<HTMLDivElement>(null);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 添加图片缓存引用
  const imageCacheRef = useRef<{ [key: string]: HTMLImageElement }>({});

  const banners = [...Array(6)]
    .map((_, index) => ({
      id: index + 1,
      logo: companyDomain?.[`logo${index + 1}`] || "",
    }))
    .filter((item) => item.logo);

  // 预加载图片函数
  const preloadImages = () => {
    banners.forEach((item) => {
      if (item.logo && !imageCacheRef.current[item.logo]) {
        const img = new Image();
        img.src = imageConfig.baseImageUrl + item.logo;
        imageCacheRef.current[item.logo] = img;
      }
    });
  };

  const handlePrev = () => {
    if (banners.length === 0 || isAnimating.value) return;

    isAnimating.value = true;
    currentIndex.value = Math.max(0, currentIndex.value - 1);
    
    // 重置自动播放计时器
    resetAutoplayTimer();
    
    // 添加短暂动画锁定
    setTimeout(() => {
      isAnimating.value = false;
    }, 300);
  };

  const handleNext = () => {
    if (banners.length === 0 || isAnimating.value) return;

    isAnimating.value = true;
    const visibleItems = getVisibleItems();
    const maxIndex = Math.max(0, banners.length - visibleItems);
    currentIndex.value = Math.min(maxIndex, currentIndex.value + 1);
    
    // 重置自动播放计时器
    resetAutoplayTimer();
    
    // 添加短暂动画锁定
    setTimeout(() => {
      isAnimating.value = false;
    }, 300);
  };

  const handleDotClick = (index: number) => {
    if (banners.length === 0 || isAnimating.value) return;
    if (index === currentIndex.value) return;

    isAnimating.value = true;
    const visibleItems = getVisibleItems();
    const maxIndex = Math.max(0, banners.length - visibleItems);
    currentIndex.value = Math.min(maxIndex, index);
    
    // 重置自动播放计时器
    resetAutoplayTimer();
    
    // 添加短暂动画锁定
    setTimeout(() => {
      isAnimating.value = false;
    }, 300);
  };

  const calculateDots = () => {
    if (banners.length === 0) return [];
    const visibleItems = getVisibleItems();
    const dotsCount = Math.max(1, banners.length - visibleItems + 1);
    return Array.from({ length: dotsCount }, (_, i) => i);
  };

  // 触摸事件处理函数
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    autoplayPaused.value = true; // 暂停自动播放
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50; // 最小滑动距离
    
    if (distance > minSwipeDistance) {
      // 向左滑动 -> 下一张
      handleNext();
    } else if (distance < -minSwipeDistance) {
      // 向右滑动 -> 上一张
      handlePrev();
    }
    
    // 重置触摸状态
    setTouchStart(0);
    setTouchEnd(0);
    autoplayPaused.value = false; // 恢复自动播放
  };

  // 自动播放功能
  const startAutoplay = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
    }
    
    autoplayTimerRef.current = setInterval(() => {
      if (!autoplayPaused.value && banners.length > getVisibleItems()) {
        // 如果到达最后一个，则回到第一个
        const visibleItems = getVisibleItems();
        const maxIndex = Math.max(0, banners.length - visibleItems);
        
        if (currentIndex.value >= maxIndex) {
          currentIndex.value = 0;
        } else {
          currentIndex.value = currentIndex.value + 1;
        }
      }
    }, 5000); // 5秒切换一次
  };

  const resetAutoplayTimer = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      startAutoplay();
    }
  };

  useEffect(() => {
    // 预加载所有图片
    preloadImages();
    
    startAutoplay();
    
    const handleResize = () => {
      const visibleItems = getVisibleItems();
      const maxIndex = Math.max(0, banners.length - visibleItems);
      if (currentIndex.value > maxIndex) {
        currentIndex.value = maxIndex;
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [banners]);

  if (banners.length === 0) {
    return null;
  }

  // 计算动态间距和按钮大小
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <div className="w-full max-w-[1440px] mx-auto my-4 md:my-10 relative overflow-hidden revert-style">
      <div className="relative flex items-center justify-center px-3 sm:px-6 md:px-[60px]">
        <button
          onClick={handlePrev}
          className={`absolute left-1 sm:left-3 md:left-5 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center z-10 shadow-card transition-all duration-200 hover:bg-neutral-100 active:scale-95 ${
            currentIndex.value === 0 || isAnimating.value
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "hover:scale-105"
          }`}
          aria-label="Previous"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-neutral-200"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div 
          className="w-full overflow-hidden relative"
          ref={swiperRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex h-40 sm:h-40 md:h-40 transition-transform duration-300 ease-[cubic-bezier(0.33,1,0.68,1)] will-change-transform translate-z-0"
            style={{
              transform: `translate3d(-${currentIndex.value * (100 / getVisibleItems())}%, 0, 0)`,
            }}
          >
            {banners.map((item) => (
              <div
                key={item.id}
                className="flex-[0_0_100%] sm:flex-[0_0_50%] md:flex-[0_0_33.333%] lg:flex-[0_0_25%] px-2 sm:px-2.5 box-border will-change-[opacity,transform] translate-z-0"
              >
                <div className="rounded-xl overflow-hidden shadow-card h-40 sm:h-40 md:h-40 w-full flex flex-col items-start relative p-3 sm:p-4 md:p-[20px_30px] will-change-transform translate-z-0 transition-transform duration-300 hover:scale-[1.02]">
                  <img
                    alt=""
                    src={imageCacheRef.current[item.logo]?.src || imageConfig.baseImageUrl + item.logo}
                    loading="lazy"
                    className="absolute top-0 left-0 w-full h-full object-contain z-0 mix-blend-luminosity will-change-opacity"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleNext}
          className={`absolute right-1 sm:right-3 md:right-5 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center z-10 shadow-card transition-all duration-200 hover:bg-neutral-100 active:scale-95 ${
            currentIndex.value >= banners.length - getVisibleItems() ||
            isAnimating.value
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "hover:scale-105"
          }`}
          aria-label="Next"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-neutral-200"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="flex justify-center items-center mt-3 md:mt-4">
        {calculateDots().map((index) => (
          <div
            key={index}
            className={`h-1.5 sm:h-2 rounded-sm mx-1 cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.33,1,0.68,1)] will-change-[width,background-color] hover:bg-opacity-40 ${
              index === currentIndex.value
                ? "w-4 sm:w-6 bg-primary"
                : "w-1.5 sm:w-2 bg-black bg-opacity-20"
            }`}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </div>
  );
}
